/* ─────────────────────────────────────────────────────
   pricing.ts — table de prix Indyanna Studio
   Extensible : ajoutez un modèle dans MODEL_GENDER_MAP
   ───────────────────────────────────────────────────── */

export type Gender = 'homme' | 'femme';
export type DeliveryWeeks = 8 | 14 | 16 | '8mie';

export interface PriceTier {
  minQty: number;
  price: number;
}

export interface DeliveryOption {
  weeks: DeliveryWeeks;
  label: string;
  tiers: PriceTier[];
}

export interface GenderPricing {
  ref: string;
  deliveries: DeliveryOption[];
}

export const PRICING: Record<Gender, GenderPricing> = {
  homme: {
    ref: 'NTH JU',
    deliveries: [
      {
        weeks: 16,
        label: '16 semaines',
        tiers: [
          { minQty: 300,  price: 6.90 },
          { minQty: 500,  price: 6.07 },
          { minQty: 750,  price: 5.43 },
          { minQty: 1000, price: 5.00 },
        ],
      },
      {
        weeks: 14,
        label: '14 semaines',
        tiers: [
          { minQty: 300,  price: 7.02 },
          { minQty: 500,  price: 6.14 },
          { minQty: 750,  price: 5.47 },
          { minQty: 1000, price: 5.08 },
        ],
      },
      {
        weeks: 8,
        label: '8 semaines',
        tiers: [
          { minQty: 100,  price: 10.96 },
          { minQty: 200,  price: 8.60  },
          { minQty: 300,  price: 7.71  },
          { minQty: 500,  price: 6.90  },
          { minQty: 750,  price: 6.34  },
          { minQty: 1000, price: 5.95  },
        ],
      },
      {
        weeks: '8mie',
        label: '8 sem. Made In Europe',
        tiers: [
          { minQty: 30,   price: 23.41 },
          { minQty: 50,   price: 19.22 },
          { minQty: 100,  price: 16.02 },
          { minQty: 200,  price: 14.28 },
          { minQty: 300,  price: 13.44 },
          { minQty: 500,  price: 12.23 },
          { minQty: 750,  price: 11.84 },
          { minQty: 1000, price: 11.06 },
        ],
      },
    ],
  },
  femme: {
    ref: 'NTF JU',
    deliveries: [
      {
        weeks: 16,
        label: '16 semaines',
        tiers: [
          { minQty: 300,  price: 6.55 },
          { minQty: 500,  price: 5.73 },
          { minQty: 750,  price: 5.10 },
          { minQty: 1000, price: 4.68 },
        ],
      },
      {
        weeks: 14,
        label: '14 semaines',
        tiers: [
          { minQty: 300,  price: 6.66 },
          { minQty: 500,  price: 5.79 },
          { minQty: 750,  price: 5.14 },
          { minQty: 1000, price: 4.76 },
        ],
      },
      {
        weeks: 8,
        label: '8 semaines',
        tiers: [
          { minQty: 100,  price: 10.58 },
          { minQty: 200,  price: 8.24  },
          { minQty: 300,  price: 7.36  },
          { minQty: 500,  price: 6.56  },
          { minQty: 750,  price: 6.01  },
          { minQty: 1000, price: 5.63  },
        ],
      },
      {
        weeks: '8mie',
        label: '8 sem. Made In Europe',
        tiers: [
          { minQty: 30,   price: 23.41 },
          { minQty: 50,   price: 19.22 },
          { minQty: 100,  price: 16.02 },
          { minQty: 200,  price: 14.28 },
          { minQty: 300,  price: 13.44 },
          { minQty: 500,  price: 12.23 },
          { minQty: 750,  price: 11.84 },
          { minQty: 1000, price: 11.06 },
        ],
      },
    ],
  },
};

/* ── Mapping modèle → genre ──────────────────────────
   Ajoutez chaque nouveau modèle ici.
   Clé : fileName du modèle (ex. "Tshirt.glb")
   ──────────────────────────────────────────────────── */
export const MODEL_GENDER_MAP: Record<string, Gender> = {
  'Tshirt.glb':       'homme',
  'TshirtFemme.glb':  'femme',
  // Ajoutez vos nouveaux modèles ici :
  // 'NouveauModele.glb': 'homme',
};

export function getModelGender(model: { fileName?: string; category?: string; name?: string } | null): Gender {
  if (!model) return 'homme';

  // 1. Correspondance exacte sur fileName
  if (model.fileName && MODEL_GENDER_MAP[model.fileName]) {
    return MODEL_GENDER_MAP[model.fileName];
  }

  // 2. Mots-clés dans la catégorie ou le nom
  const text = `${model.category || ''} ${model.name || ''}`.toLowerCase();
  if (/femme|woman|female|féminin/.test(text)) return 'femme';
  if (/homme|man\b|male|masculin/.test(text)) return 'homme';

  return 'homme'; // défaut
}

/** Retourne le prix unitaire pour une quantité et un délai donnés, ou null si hors grille */
export function getUnitPrice(gender: Gender, weeks: DeliveryWeeks, qty: number): number | null {
  const delivery = PRICING[gender].deliveries.find(d => d.weeks === weeks);
  if (!delivery) return null;
  const tiers = [...delivery.tiers].sort((a, b) => b.minQty - a.minQty);
  const tier = tiers.find(t => qty >= t.minQty);
  return tier ? tier.price : null;
}

/* ─────────────────────────────────────────────────────
   Tarifs tour de cou
   Sublimation quadrichromie incluse, PU HT.
   Deux origines aux grilles independantes :
     - Asie   : refs DA3, delais 5 et 14 semaines, au choix
                polyester ou polyester recycle RPET, avec ou
                sans bande reflechissante.
     - Europe : ref GR8, 130 g/m2, delai 4 semaines, une
                seule grille (ni RPET ni bande reflechissante).

   Lecture des "x" du tarif fournisseur :
     - "x" sous 300 pieces sur les lignes "avec bande" = option
       reellement indisponible a ce volume -> aucun prix rendu.
     - "x" a 5000 pieces = pas de palier supplementaire ; le
       palier "3000 et +" continue de s'appliquer au-dela.
   ───────────────────────────────────────────────────── */

export type ColMaterial = 'polyester' | 'rpet';
export type ColOrigin = 'asie' | 'europe';
export type ColDeliveryWeeks = 4 | 5 | 14;

export interface ColPriceGrid {
  origin: ColOrigin;
  weeks: ColDeliveryWeeks;
  material: ColMaterial;
  reflective: boolean;
  ref: string;
  tiers: PriceTier[];
}

export const COL_PRICING: ColPriceGrid[] = [
  /* ── Asie — 5 semaines ─────────────────────────────── */
  {
    origin: 'asie', weeks: 5, material: 'polyester', reflective: false, ref: 'DA3 LA',
    tiers: [
      { minQty: 50,   price: 5.76 },
      { minQty: 75,   price: 4.55 },
      { minQty: 100,  price: 3.40 },
      { minQty: 250,  price: 2.32 },
      { minQty: 300,  price: 2.14 },
      { minQty: 400,  price: 1.99 },
      { minQty: 500,  price: 1.62 },
      { minQty: 750,  price: 1.43 },
      { minQty: 1000, price: 1.22 },
      { minQty: 2000, price: 1.12 },
      { minQty: 3000, price: 1.06 },
      { minQty: 5000, price: 0.94 },
    ],
  },
  {
    origin: 'asie', weeks: 5, material: 'polyester', reflective: true, ref: 'DA3BR LA',
    tiers: [
      { minQty: 300,  price: 2.64 },
      { minQty: 400,  price: 2.47 },
      { minQty: 500,  price: 2.07 },
      { minQty: 750,  price: 1.86 },
      { minQty: 1000, price: 1.59 },
      { minQty: 2000, price: 1.45 },
      { minQty: 3000, price: 1.34 },
    ],
  },
  {
    origin: 'asie', weeks: 5, material: 'rpet', reflective: false, ref: 'DA3 RPET LA',
    tiers: [
      { minQty: 50,   price: 6.15 },
      { minQty: 75,   price: 4.88 },
      { minQty: 100,  price: 3.61 },
      { minQty: 250,  price: 2.53 },
      { minQty: 300,  price: 2.30 },
      { minQty: 400,  price: 2.14 },
      { minQty: 500,  price: 1.74 },
      { minQty: 750,  price: 1.57 },
      { minQty: 1000, price: 1.41 },
      { minQty: 2000, price: 1.24 },
      { minQty: 3000, price: 1.17 },
    ],
  },
  {
    origin: 'asie', weeks: 5, material: 'rpet', reflective: true, ref: 'DA3BR RPET LA',
    tiers: [
      { minQty: 300,  price: 2.88 },
      { minQty: 400,  price: 2.70 },
      { minQty: 500,  price: 2.26 },
      { minQty: 750,  price: 2.01 },
      { minQty: 1000, price: 1.84 },
      { minQty: 2000, price: 1.64 },
      { minQty: 3000, price: 1.51 },
    ],
  },

  /* ── Asie — 14 semaines ────────────────────────────── */
  {
    origin: 'asie', weeks: 14, material: 'polyester', reflective: false, ref: 'DA3 LA',
    tiers: [
      { minQty: 1000, price: 1.08 },
      { minQty: 2000, price: 0.93 },
      { minQty: 3000, price: 0.87 },
      { minQty: 5000, price: 0.70 },
    ],
  },
  {
    origin: 'asie', weeks: 14, material: 'polyester', reflective: true, ref: 'DA3BR LA',
    tiers: [
      { minQty: 1000, price: 1.41 },
      { minQty: 2000, price: 1.25 },
      { minQty: 3000, price: 1.18 },
    ],
  },
  {
    origin: 'asie', weeks: 14, material: 'rpet', reflective: false, ref: 'DA3 RPET LA',
    tiers: [
      { minQty: 1000, price: 1.34 },
      { minQty: 2000, price: 1.14 },
      { minQty: 3000, price: 1.07 },
    ],
  },
  {
    origin: 'asie', weeks: 14, material: 'rpet', reflective: true, ref: 'DA3BR RPET LA',
    tiers: [
      { minQty: 1000, price: 1.67 },
      { minQty: 2000, price: 1.46 },
      { minQty: 3000, price: 1.38 },
    ],
  },

  /* ── Europe — 4 semaines ───────────────────────────── */
  {
    origin: 'europe', weeks: 4, material: 'polyester', reflective: false, ref: 'GR8',
    tiers: [
      { minQty: 100,  price: 3.72 },
      { minQty: 200,  price: 2.40 },
      { minQty: 300,  price: 2.21 },
      { minQty: 500,  price: 1.95 },
      { minQty: 750,  price: 1.83 },
      { minQty: 1000, price: 1.75 },
      { minQty: 2000, price: 1.63 },
      { minQty: 3000, price: 1.54 },
    ],
  },
];

export interface ColPriceQuery {
  origin: ColOrigin;
  weeks: ColDeliveryWeeks;
  material: ColMaterial;
  reflective: boolean;
  qty: number;
}

export type ColPriceResult =
  /** Prix trouve. */
  | { ok: true; unitPrice: number; ref: string; minQty: number }
  /** Cette combinaison d'options n'existe pas au tarif (ex. RPET en Europe). */
  | { ok: false; reason: 'combinaisonIndisponible' }
  /** La combinaison existe mais pas a cette quantite. */
  | { ok: false; reason: 'quantiteInsuffisante'; minQty: number };

/** Grille correspondant exactement a une combinaison d'options, si elle existe. */
export function findColGrid(
  query: Omit<ColPriceQuery, 'qty'>
): ColPriceGrid | null {
  return (
    COL_PRICING.find(
      grid =>
        grid.origin === query.origin &&
        grid.weeks === query.weeks &&
        grid.material === query.material &&
        grid.reflective === query.reflective
    ) || null
  );
}

/**
 * Prix unitaire d'un tour de cou. Rend un resultat explicite plutot que null
 * pour que l'interface puisse dire POURQUOI il n'y a pas de prix : option
 * indisponible, ou quantite sous le minimum de la grille.
 */
export function getColUnitPrice(query: ColPriceQuery): ColPriceResult {
  const grid = findColGrid(query);
  if (!grid || grid.tiers.length === 0) {
    return { ok: false, reason: 'combinaisonIndisponible' };
  }
  const minQty = Math.min(...grid.tiers.map(t => t.minQty));
  if (query.qty < minQty) {
    return { ok: false, reason: 'quantiteInsuffisante', minQty };
  }
  // Palier le plus eleve atteint : "3000 et +" couvre donc aussi 5000+.
  const tier = [...grid.tiers]
    .sort((a, b) => b.minQty - a.minQty)
    .find(t => query.qty >= t.minQty)!;
  return { ok: true, unitPrice: tier.price, ref: grid.ref, minQty };
}

/** Options reellement proposables, pour griser le reste dans l'interface. */
export function getColAvailableOptions(origin: ColOrigin) {
  const grids = COL_PRICING.filter(g => g.origin === origin);
  return {
    weeks: [...new Set(grids.map(g => g.weeks))].sort((a, b) => a - b),
    materials: [...new Set(grids.map(g => g.material))],
    reflectiveAvailable: grids.some(g => g.reflective),
  };
}

/* ── Mapping modele → type de produit ─────────────────
   Sans cela un tour de cou tomberait silencieusement sur
   la grille t-shirt homme via getModelGender().
   ──────────────────────────────────────────────────── */

export type ProductType = 'tshirt' | 'tourDeCou';

export const MODEL_PRODUCT_MAP: Record<string, ProductType> = {
  'TourDeCou.glb': 'tourDeCou',
};

export function getProductType(model: { fileName?: string } | null): ProductType {
  if (model?.fileName && MODEL_PRODUCT_MAP[model.fileName]) {
    return MODEL_PRODUCT_MAP[model.fileName];
  }
  return 'tshirt';
}
